<template>
    <RichTextContent
        ref="richText"
        dusk="AddOnBody"
        :lang="contentLanguage"
        class="AddOnBody"
        :html="content || ''"
        :labels="embedLabels"
        :audio-hosts="audioEmbedHosts"
        inline-embeds
    />
</template>

<script>
'use strict';

import { mapGetters, mapState } from 'vuex';
import RichTextContent from '../Components/RichTextContent.vue';

/**
 * The body of an add-on: Trix legacy HTML with its attachments and galleries,
 * or the rich editor's output, both rendered through RichTextContent. Unlike a
 * note, its embeds are framed right away: the panel reads one piece at a time,
 * so there is no list of players to hold back.
 */
export default {
    name: 'AddOnBody',
    components: { RichTextContent },
    props: {
        content: {
            type: String,
            default: '',
        },
        contentLanguage: {
            type: String,
            default: '',
        },
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['richTextEmbedHosts']),
        embedLabels() {
            return this.localizations[this.lang]?.richText?.embed;
        },
        audioEmbedHosts() {
            return this.richTextEmbedHosts?.audio ?? [];
        },
    },
    methods: {
        releaseEmbeds() {
            this.$refs.richText.releaseEmbeds();
        },
    },
};
</script>

<style scoped>
.AddOnBody {
    @apply text-[0.9375em] leading-relaxed text-theme-text;
}

/* Sized in em so the whole column follows the base size above. */
.AddOnBody :deep(h1) {
    @apply mb-3 mt-5 font-semibold tracking-tight;
    font-size: 1.5em;
}

.AddOnBody :deep(h2) {
    @apply mb-2 mt-4 font-semibold tracking-tight;
    font-size: 1.25em;
}

.AddOnBody :deep(h3) {
    @apply mb-2 mt-4 font-semibold tracking-tight;
    font-size: 1.125em;
}

.AddOnBody :deep(h4),
.AddOnBody :deep(h5),
.AddOnBody :deep(h6) {
    @apply mb-2 mt-3 font-semibold tracking-tight;
    font-size: 1em;
}

.AddOnBody :deep(p) {
    @apply mb-4;
}

.AddOnBody :deep(ul),
.AddOnBody :deep(ol) {
    @apply mb-4;
}

.AddOnBody :deep(li) {
    @apply mb-2;
}

.AddOnBody :deep(blockquote) {
    @apply my-4 border-l-4 border-theme-border pl-4 italic;
}

.AddOnBody :deep(code) {
    @apply rounded bg-theme-icon-bg px-1 py-0.5;
    font-size: 0.875em;
}

.AddOnBody :deep(pre) {
    @apply my-4 overflow-x-auto rounded bg-theme-icon-bg p-4;
    font-size: 0.875em;
}

.AddOnBody :deep(a) {
    @apply text-theme-accent;
}

.AddOnBody :deep(strong) {
    @apply font-semibold;
}

.AddOnBody :deep(em) {
    @apply italic;
}

.AddOnBody :deep(hr) {
    @apply my-6 border-theme-border;
}

.AddOnBody :deep(aside) {
    @apply mx-0 my-3 rounded border border-theme-border bg-theme-icon-bg p-3;
}

/* The panel is the limit for authored video. */
.AddOnBody :deep(video) {
    @apply h-auto max-w-full;
}

.AddOnBody :deep(table) {
    @apply block max-w-full overflow-x-auto;
}

/* A figure the sanitizer keeps: what the Trix rules below give an attachment
   by class, given by tag, so an edited body looks as it did. */
.AddOnBody :deep(figure) {
    @apply m-0 max-w-full text-center;
}

.AddOnBody :deep(figure a) {
    @apply text-inherit no-underline hover:text-inherit;
}

.AddOnBody :deep(figcaption) {
    @apply p-0 text-center leading-tight text-theme-toolbar-icon;
    font-size: 0.875em;
}

/* Trix attachments */
.AddOnBody :deep(.attachment) {
    @apply relative m-0 inline-block max-w-full p-0;
}

.AddOnBody :deep(.attachment a) {
    @apply text-inherit no-underline hover:text-inherit;
}

.AddOnBody :deep(.attachment__caption) {
    @apply p-0 text-center;
}

.AddOnBody :deep(.attachment__caption .attachment__name + .attachment__size::before) {
    content: ' · ';
}

.AddOnBody :deep(.attachment--preview) {
    @apply w-full text-center;
}

.AddOnBody :deep(.attachment--preview .attachment__caption) {
    @apply leading-tight text-theme-toolbar-icon;
    font-size: 0.875em;
}

.AddOnBody :deep(.attachment--file) {
    @apply mx-0.5 mb-0.5 rounded border border-theme-border px-4 py-1.5 leading-none text-theme-text;
}

.AddOnBody :deep(.attachment-gallery) {
    @apply relative m-0 flex flex-wrap p-0;
}

.AddOnBody :deep(.attachment-gallery .attachment) {
    @apply max-w-[33.333%] flex-1 basis-1/3 px-2;
}

.AddOnBody :deep(.attachment-gallery.attachment-gallery--2 .attachment),
.AddOnBody :deep(.attachment-gallery.attachment-gallery--4 .attachment) {
    @apply max-w-[50%] basis-1/2;
}
</style>
